import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {boolean} */
  #preserveManualLastOptionSelection = false;

  /** @type {string[] | null} */
  #manualSelectedOptionValues = null;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.querySelectorAll('.variant-option__button-label').forEach((label) => {
      label.addEventListener('mouseover', this.optionLabelHovered.bind(this));
      label.addEventListener('mouseout', this.optionLabelMouseOut.bind(this));
    });

    if (this.dataset.templateProductMatch === 'true') {
      const selectionChanged = this.#syncPdpSelection({
        cascadeLastOption: true,
        changedFieldsetIndex: null,
        dispatchChange: false,
      });
      const selectedOption = this.selectedOption;

      if (selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement) {
        if (selectionChanged) {
          selectedOption.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          const state = this.#readVariantPickerState();
          const urlVariantId = new URL(window.location.href).searchParams.get('variant');
          if (state?.selectedVariant?.id && urlVariantId && urlVariantId !== String(state.selectedVariant.id)) {
            this.fetchUpdatedSection(this.buildRequestUrl(selectedOption));
          }
        }
      }
    }
  }

  /**
   * @param {Element} group
   * @returns {string | null}
   */
  #getCheckedOptionValue(group) {
    if (group.matches('fieldset')) {
      const checked = group.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : null;
    }

    const select = group.querySelector('select');
    return select instanceof HTMLSelectElement ? select.value : null;
  }

  /**
   * @param {Element} group
   * @returns {string[]}
   */
  #getOptionValuesInOrder(group) {
    if (group.matches('fieldset')) {
      return Array.from(group.querySelectorAll('input[type="radio"]')).map((input) =>
        input instanceof HTMLInputElement ? input.value : ''
      );
    }

    const select = group.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      return Array.from(select.options).map((option) => option.value);
    }

    return [];
  }

  /**
   * @param {Record<string, unknown>} variant
   * @param {string[]} fixedOptions
   * @param {number} lastOptionIndex
   * @returns {boolean}
   */
  #variantMatchesFixedOptions(variant, fixedOptions, lastOptionIndex) {
    const options = /** @type {string[] | undefined} */ (variant.options);
    if (!options) return false;

    for (let i = 0; i < lastOptionIndex; i++) {
      if (this.#normalizeOptionValue(options[i]) !== this.#normalizeOptionValue(fixedOptions[i])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Keep earlier options (e.g. color), cascade only the last option (e.g. size).
   * @param {string[]} fixedOptions
   * @param {string} preferredLastValue
   * @param {string[]} lastOptionValuesInOrder
   * @param {Record<string, unknown>[]} variants
   * @param {number} lastOptionIndex
   * @returns {Record<string, unknown> | null}
   */
  #resolveCascadeLastOption(
    fixedOptions,
    preferredLastValue,
    lastOptionValuesInOrder,
    variants,
    lastOptionIndex
  ) {
    const tryLastValues = [preferredLastValue];

    for (const value of lastOptionValuesInOrder) {
      if (this.#normalizeOptionValue(value) !== this.#normalizeOptionValue(preferredLastValue)) {
        tryLastValues.push(value);
      }
    }

    for (const lastValue of tryLastValues) {
      for (const variant of variants) {
        if (variant.available === false) continue;

        const options = /** @type {string[] | undefined} */ (variant.options);
        if (!options) continue;
        if (!this.#variantMatchesFixedOptions(variant, fixedOptions, lastOptionIndex)) continue;
        if (this.#normalizeOptionValue(options[lastOptionIndex]) !== this.#normalizeOptionValue(lastValue)) {
          continue;
        }

        return variant;
      }
    }

    return null;
  }

  /**
   * @param {Element} group
   * @param {string} optionValue
   * @returns {boolean}
   */
  #setGroupSelection(group, optionValue) {
    if (group.matches('fieldset')) {
      const radios = /** @type {HTMLInputElement[]} */ (
        Array.from(group.querySelectorAll('input[type="radio"]'))
      );
      const previouslyChecked = radios.find((radio) => radio.checked);

      radios.forEach((radio) => {
        const isMatch =
          this.#normalizeOptionValue(radio.value) === this.#normalizeOptionValue(optionValue);
        radio.checked = isMatch;
      });

      return previouslyChecked !== radios.find((radio) => radio.checked);
    }

    const select = group.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) return false;

    const previousValue = select.value;
    for (const option of select.options) {
      const isMatch =
        this.#normalizeOptionValue(option.value) === this.#normalizeOptionValue(optionValue);
      option.selected = isMatch;
      if (isMatch) select.value = option.value;
    }

    return previousValue !== select.value;
  }

  /**
   * @returns {Element[]}
   */
  #getOptionGroups() {
    const form = this.querySelector('.variant-picker__form');
    if (!form) return [];

    return Array.from(form.children).filter(
      (child) => child.matches('fieldset') || child.classList.contains('variant-option--dropdowns')
    );
  }

  /**
   * @returns {string[]}
   */
  #getCurrentSelectedOptionValuesFromDom() {
    const values = this.#getOptionGroups().map((group) => this.#getCheckedOptionValue(group));
    if (values.some((value) => value == null)) return [];

    return /** @type {string[]} */ (values);
  }

  /**
   * @returns {Record<string, unknown> | null}
   */
  #findVariantForCurrentSelection() {
    const state = this.#readVariantPickerState();
    if (!state) return null;

    const optionGroups = this.#getOptionGroups();
    const optionCount = optionGroups.length;
    if (optionCount === 0) return null;

    const selectedValues = this.#getCurrentSelectedOptionValuesFromDom();
    if (selectedValues.length !== optionCount) return null;

    return (
      state.variants.find((variant) => {
        const options = /** @type {string[] | undefined} */ (variant.options);
        if (!options || options.length !== optionCount) return false;

        return options.every(
          (opt, index) =>
            this.#normalizeOptionValue(opt) === this.#normalizeOptionValue(selectedValues[index])
        );
      }) ?? null
    );
  }

  /**
   * @param {string[]} optionValues
   */
  #applyOptionValuesToPicker(optionValues) {
    const optionGroups = this.#getOptionGroups();
    optionGroups.forEach((group, index) => {
      const optionValue = optionValues[index];
      if (optionValue) this.#setGroupSelection(group, optionValue);
    });
  }

  #normalizeCheckedDataAttributes() {
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      radios.forEach((radio) => {
        if (radio instanceof HTMLInputElement) {
          radio.dataset.currentChecked = radio.checked ? 'true' : 'false';
          radio.dataset.previousChecked = 'false';
        }
      });
    });
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.cascadeLastOption]
   * @param {number | null} [options.changedFieldsetIndex]
   * @param {boolean} [options.dispatchChange]
   * @returns {boolean} Whether the visible selection changed.
   */
  #syncPdpSelection({
    cascadeLastOption = true,
    changedFieldsetIndex = null,
    dispatchChange = true,
  } = {}) {
    const state = this.#readVariantPickerState();
    if (!state) return false;

    const form = this.querySelector('.variant-picker__form');
    if (!form) return false;

    const optionGroups = this.#getOptionGroups();
    const optionCount = optionGroups.length;
    if (optionCount === 0) return false;

    const lastOptionIndex = optionCount - 1;
    /** @type {string[]} */
    const fixedOptions = [];

    for (let i = 0; i < lastOptionIndex; i++) {
      const group = optionGroups[i];
      if (!group) return false;
      const value = this.#getCheckedOptionValue(group);
      if (!value) return false;
      fixedOptions.push(value);
    }

    const lastGroup = optionGroups[lastOptionIndex];
    if (!lastGroup) return false;
    const currentLastValue = this.#getCheckedOptionValue(lastGroup) ?? '';
    const lastOptionValuesInOrder = this.#getOptionValuesInOrder(lastGroup);

    const currentCombo = state.variants.find((variant) => {
      const options = /** @type {string[] | undefined} */ (variant.options);
      if (!options || options.length !== optionCount) return false;

      return options.every(
        (opt, index) => this.#normalizeOptionValue(opt) === this.#normalizeOptionValue(
          index < lastOptionIndex ? fixedOptions[index] : currentLastValue
        )
      );
    });

    const urlVariantId = new URL(window.location.href).searchParams.get('variant');

    let shouldCascade =
      cascadeLastOption &&
      optionCount > 1 &&
      (changedFieldsetIndex === null || changedFieldsetIndex < lastOptionIndex);

    // Keep manual OOS selection when URL variant matches the current combination.
    if (urlVariantId && currentCombo && String(currentCombo.id) === urlVariantId) {
      shouldCascade = false;
    }

    if (this.#preserveManualLastOptionSelection) {
      shouldCascade = false;
    }

    /** @type {Record<string, unknown> | null} */
    let targetVariant = currentCombo ?? state.selectedVariant;

    if (shouldCascade && currentCombo?.available !== true) {
      targetVariant =
        this.#resolveCascadeLastOption(
          fixedOptions,
          currentLastValue,
          lastOptionValuesInOrder,
          state.variants,
          lastOptionIndex
        ) ?? currentCombo ?? state.selectedVariant;
    }

    if (!targetVariant) return false;

    const variantOptions = /** @type {string[] | undefined} */ (targetVariant.options);
    if (!variantOptions) return false;

    if (this.#preserveManualLastOptionSelection && this.#manualSelectedOptionValues?.length) {
      this.#applyOptionValuesToPicker(this.#manualSelectedOptionValues);
      this.#normalizeCheckedDataAttributes();
      return false;
    }

    let selectionChanged = false;

    optionGroups.forEach((group, index) => {
      const optionValue = variantOptions[index];
      if (!optionValue) return;
      if (this.#setGroupSelection(group, optionValue)) selectionChanged = true;
    });

    this.#normalizeCheckedDataAttributes();
    this.#syncPdpInitialVariantUrl(targetVariant);

    if (selectionChanged && dispatchChange) {
      const changeTarget = this.querySelector('fieldset input:checked, select.variant-option__select');
      if (changeTarget instanceof HTMLInputElement || changeTarget instanceof HTMLSelectElement) {
        changeTarget.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    return selectionChanged;
  }

  /**
   * @param {string | undefined | null} value
   * @returns {string}
   */
  #normalizeOptionValue(value) {
    return String(value ?? '').trim().toLowerCase();
  }

  /**
   * @param {string | undefined} textContent
   * @returns {Record<string, unknown> | null}
   */
  #parseVariantFromState(textContent) {
    if (!textContent) return null;

    try {
      const parsed = JSON.parse(textContent);
      if (parsed && typeof parsed === 'object' && 'selectedVariant' in parsed) {
        return /** @type {Record<string, unknown>} */ (parsed.selectedVariant);
      }
      if (parsed && typeof parsed === 'object' && 'id' in parsed) {
        return /** @type {Record<string, unknown>} */ (parsed);
      }
    } catch {
      return null;
    }

    return null;
  }

  /**
   * @returns {{ selectedVariant: Record<string, unknown>, variants: Record<string, unknown>[] } | null}
   */
  #readVariantPickerState() {
    const script = this.querySelector(
      'script[type="application/json"][data-variant-picker-state], script[type="application/json"]'
    );
    const textContent = script?.textContent;
    if (!textContent) return null;

    try {
      const parsed = JSON.parse(textContent);
      if (parsed?.selectedVariant && Array.isArray(parsed.variants)) {
        return {
          selectedVariant: parsed.selectedVariant,
          variants: parsed.variants,
        };
      }
      if (parsed?.id) {
        return {
          selectedVariant: parsed,
          variants: [parsed],
        };
      }
    } catch {
      return null;
    }

    return null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.variantChanged.bind(this));
    this.querySelectorAll('.variant-option__button-label').forEach((label) => {
      label.removeEventListener('mouseover', this.optionLabelHovered.bind(this));
      label.removeEventListener('mouseout', this.optionLabelMouseOut.bind(this));
    });
  }

  /**
   * Aligns the PDP URL with the server-selected initial variant when the default
   * variant was out of stock and a purchasable variant was auto-selected.
   * @param {Record<string, unknown>} [variant]
   */
  #syncPdpInitialVariantUrl(variant) {
    if (!variant?.id) {
      const parsedVariant = this.#parseVariantFromState(
        this.querySelector(
          'script[type="application/json"][data-variant-picker-state], script[type="application/json"]'
        )?.textContent
      );
      if (parsedVariant) variant = parsedVariant;
    }

    if (!variant?.id) return;

    const url = new URL(window.location.href);
    const urlVariantId = url.searchParams.get('variant');

    if (urlVariantId === String(variant.id)) return;

    url.searchParams.set('variant', String(variant.id));
    requestYieldCallback(() => {
      history.replaceState({}, '', url.toString());
    });
  }

  /**
   * Handles the option label hover event.
   * @param {Event} event - The option label hover event.
   */
  optionLabelHovered(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const optionLabel = event.target.closest('.variant-option__button-label');
    if (!(optionLabel instanceof HTMLElement)) return;
    const optionLabelValue = optionLabel.dataset.optionLabelValue;

    const variantOptionButtons = optionLabel.closest('.variant-option--buttons');
    if (!(variantOptionButtons instanceof HTMLElement)) return;
    variantOptionButtons.classList.add('variant-option--buttons-hovered');
    const labelValue = variantOptionButtons.querySelector('.variant-option__label-value');
    if (labelValue instanceof HTMLElement) {
      labelValue.textContent = optionLabelValue || null;
    }
  }

  /**
   * Handles the option label mouse out event.
   * @param {Event} event - The option label mouse out event.
   */
  optionLabelMouseOut(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const optionLabel = event.target.closest('.variant-option__button-label');
    if (!(optionLabel instanceof HTMLElement)) return;

    const variantOptionButtons = optionLabel.closest('.variant-option--buttons');
    if (!(variantOptionButtons instanceof HTMLElement)) return;
    variantOptionButtons.classList.remove('variant-option--buttons-hovered');
    const labelValue = variantOptionButtons.querySelector('.variant-option__label-value');
    if (labelValue instanceof HTMLElement) {
      labelValue.textContent = labelValue.dataset.optionLabelValue || null;
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    if (isOnProductPage && event.target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(event.target.dataset.fieldsetIndex ?? '', 10);
      const fieldsetsRef = this.refs.fieldsets;
      const fieldsets = Array.isArray(fieldsetsRef)
        ? fieldsetsRef
        : fieldsetsRef
          ? [fieldsetsRef]
          : [];
      const lastFieldsetIndex = fieldsets.length - 1;

      if (!Number.isNaN(fieldsetIndex) && fieldsetIndex < lastFieldsetIndex) {
        this.#preserveManualLastOptionSelection = false;
        this.#manualSelectedOptionValues = null;
        this.#syncPdpSelection({
          cascadeLastOption: true,
          changedFieldsetIndex: fieldsetIndex,
          dispatchChange: false,
        });
      } else if (!Number.isNaN(fieldsetIndex) && fieldsetIndex === lastFieldsetIndex) {
        this.#preserveManualLastOptionSelection = true;
        this.#manualSelectedOptionValues = this.#getCurrentSelectedOptionValuesFromDom();
        this.#normalizeCheckedDataAttributes();
      }
    }

    const selectedOption = this.selectedOption;
    if (!selectedOption) return;

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const comboVariant = this.#findVariantForCurrentSelection();
    const variantId =
      comboVariant?.id != null ? String(comboVariant.id) : selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
        history.replaceState({}, '', url.toString());
      } else {
        url.searchParams.delete('variant');
        history.replaceState({}, '', url.toString());
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    const selectedVariantId =
      this.#findVariantForCurrentSelection()?.id ?? selectedOption.dataset.variantId;
    if (this.dataset.templateProductMatch === 'true' && selectedVariantId) {
      params.push(`variant=${selectedVariantId}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      if (this.closest('.cart-drawer') || this.closest('.product-card-hover')) {
        return `${productUrl}?section_id=section-rendering-product-card-hover&${params.join('&')}`;

      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`${this.tagName.toLowerCase()} script[type="application/json"]`)?.textContent;
        const variantData = this.#parseVariantFromState(textContent);
        if (!variantData) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(/** @type {any} */ (variantData), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    if (this.dataset.templateProductMatch === 'true') {
      if (this.#preserveManualLastOptionSelection && this.#manualSelectedOptionValues?.length) {
        this.#applyOptionValuesToPicker(this.#manualSelectedOptionValues);
        this.#normalizeCheckedDataAttributes();
      } else {
        this.#syncPdpSelection({ cascadeLastOption: true, changedFieldsetIndex: null, dispatchChange: false });
      }
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
